import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sites } from '../api/methods';
export const siteKeys = {
    all: ['sites'],
    detail: (siteId) => ['sites', 'detail', siteId],
    byCard: (cardId) => ['sites', 'byCard', cardId],
    syncStatus: (siteId) => ['sites', siteId, 'syncStatus'],
    notesHistory: (siteId) => ['sites', siteId, 'notesHistory'],
    attachments: (siteId) => ['sites', siteId, 'attachments'],
    labels: (siteId) => ['sites', siteId, 'labels'],
};
export function useSite(siteId) {
    return useQuery({
        queryKey: siteKeys.detail(siteId),
        queryFn: () => sites.get(siteId),
        enabled: !!siteId,
    });
}
export function useSiteByCardId(cardId) {
    return useQuery({
        queryKey: siteKeys.byCard(cardId),
        queryFn: () => sites.getByCardId(cardId),
        enabled: !!cardId,
    });
}
export function useSyncSite(siteId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => sites.sync(siteId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: siteKeys.detail(siteId) });
            queryClient.invalidateQueries({ queryKey: siteKeys.attachments(siteId) });
            queryClient.invalidateQueries({ queryKey: siteKeys.syncStatus(siteId) });
        },
    });
}
export function usePurgeArchivedTickets(siteId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => sites.purgeArchivedTickets(siteId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: siteKeys.detail(siteId) });
        },
    });
}
export function useUpdateSiteNotes(siteId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (notes) => sites.updateNotes(siteId, notes),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: siteKeys.detail(siteId) });
            queryClient.invalidateQueries({
                queryKey: siteKeys.notesHistory(siteId),
            });
        },
    });
}
/** #1064: the site's default business line (QuickBooks Class) for new tickets. Office roles. */
export function useSetSiteDefaultBusinessLine(siteId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (defaultBusinessLine) => sites.setDefaultBusinessLine(siteId, defaultBusinessLine),
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: siteKeys.detail(siteId) });
        },
    });
}
function optimisticCustomField(field, value) {
    if (value === null || value === '') {
        return { ...field, value: null, id_value: null, display_value: '' };
    }
    if (field.type === 'list') {
        const option = field.options?.find((candidate) => candidate.id === value);
        const optionValue = option?.value;
        const displayValue = typeof optionValue === 'string'
            ? optionValue
            : optionValue?.text || String(value);
        return {
            ...field,
            value: null,
            id_value: String(value),
            display_value: displayValue,
        };
    }
    if (field.type === 'checkbox') {
        const checked = String(Boolean(value));
        return {
            ...field,
            value: { checked },
            id_value: null,
            display_value: checked === 'true' ? 'Yes' : 'No',
        };
    }
    if (field.type === 'date') {
        const date = `${String(value)}T00:00:00.000Z`;
        return {
            ...field,
            value: { date },
            id_value: null,
            display_value: date,
        };
    }
    const stringValue = String(value);
    return {
        ...field,
        value: { [field.type]: stringValue },
        id_value: null,
        display_value: stringValue,
    };
}
export function useUpdateSiteCustomField(siteId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ fieldId, value, }) => sites.updateCustomField(siteId, fieldId, value),
        onMutate: async ({ fieldId, value }) => {
            await queryClient.cancelQueries({ queryKey: siteKeys.detail(siteId) });
            const previousSite = queryClient.getQueryData(siteKeys.detail(siteId));
            queryClient.setQueryData(siteKeys.detail(siteId), (site) => site ? ({
                ...site,
                custom_fields: site.custom_fields?.map((field) => (field.field_id === fieldId ? optimisticCustomField(field, value) : field)),
            }) : site);
            return { previousSite };
        },
        onError: (_error, _input, context) => {
            queryClient.setQueryData(siteKeys.detail(siteId), context?.previousSite);
        },
        onSuccess: (response) => {
            queryClient.setQueryData(siteKeys.detail(siteId), (site) => site ? ({
                ...site,
                custom_fields: site.custom_fields?.map((field) => (field.field_id === response.custom_field.field_id
                    ? response.custom_field
                    : field)),
            }) : site);
            queryClient.invalidateQueries({ queryKey: siteKeys.detail(siteId) });
        },
    });
}
export function useSiteLabels(siteId, enabled = true) {
    return useQuery({
        queryKey: siteKeys.labels(siteId),
        queryFn: () => sites.getLabels(siteId),
        enabled: Boolean(siteId) && enabled,
    });
}
function replaceSiteLabels(site, labels) {
    return site ? { ...site, trello_labels: labels } : site;
}
export function useAddSiteLabel(siteId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input) => sites.addLabel(siteId, input),
        onMutate: async (input) => {
            await Promise.all([
                queryClient.cancelQueries({ queryKey: siteKeys.detail(siteId) }),
                queryClient.cancelQueries({ queryKey: siteKeys.labels(siteId) }),
            ]);
            const previousSite = queryClient.getQueryData(siteKeys.detail(siteId));
            const previousLabels = queryClient.getQueryData(siteKeys.labels(siteId));
            const label = 'labelId' in input
                ? previousLabels?.labels.find((candidate) => candidate.id === input.labelId)
                : {
                    id: `optimistic-${Date.now()}`,
                    name: input.name,
                    color: input.color,
                    is_on_card: true,
                };
            if (label) {
                const optimisticLabel = {
                    id: label.id,
                    name: label.name,
                    color: label.color,
                    managed_by_ticket_flags: label.managed_by_ticket_flags,
                };
                queryClient.setQueryData(siteKeys.detail(siteId), (site) => {
                    const current = site?.trello_labels || [];
                    return current.some((candidate) => candidate.id === optimisticLabel.id)
                        ? site
                        : replaceSiteLabels(site, [...current, optimisticLabel]);
                });
                queryClient.setQueryData(siteKeys.labels(siteId), (response) => {
                    if (!response)
                        return response;
                    const exists = response.labels.some((candidate) => candidate.id === optimisticLabel.id);
                    return {
                        ...response,
                        labels: exists
                            ? response.labels.map((candidate) => (candidate.id === optimisticLabel.id ? { ...candidate, is_on_card: true } : candidate))
                            : [...response.labels, { ...optimisticLabel, is_on_card: true }],
                        card_labels: [
                            ...response.card_labels.filter((candidate) => candidate.id !== optimisticLabel.id),
                            optimisticLabel,
                        ],
                    };
                });
            }
            return { previousSite, previousLabels };
        },
        onError: (_error, _input, context) => {
            queryClient.setQueryData(siteKeys.detail(siteId), context?.previousSite);
            queryClient.setQueryData(siteKeys.labels(siteId), context?.previousLabels);
        },
        onSuccess: (response) => {
            queryClient.setQueryData(siteKeys.detail(siteId), (site) => replaceSiteLabels(site, response.labels));
            queryClient.setQueryData(siteKeys.labels(siteId), (current) => ({
                labels: (current?.labels || response.labels.map((label) => ({ ...label, is_on_card: true })))
                    .map((label) => ({
                    ...label,
                    is_on_card: response.labels.some((cardLabel) => cardLabel.id === label.id),
                })),
                card_labels: response.labels,
                warning: response.warning,
            }));
            // Labels are already written above via setQueryData; only the site detail
            // needs a background refetch. Invalidating siteKeys.all churned every site
            // query for no reason.
            queryClient.invalidateQueries({ queryKey: siteKeys.detail(siteId) });
        },
    });
}
export function useRemoveSiteLabel(siteId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (labelId) => sites.removeLabel(siteId, labelId),
        onMutate: async (labelId) => {
            await Promise.all([
                queryClient.cancelQueries({ queryKey: siteKeys.detail(siteId) }),
                queryClient.cancelQueries({ queryKey: siteKeys.labels(siteId) }),
            ]);
            const previousSite = queryClient.getQueryData(siteKeys.detail(siteId));
            const previousLabels = queryClient.getQueryData(siteKeys.labels(siteId));
            queryClient.setQueryData(siteKeys.detail(siteId), (site) => (replaceSiteLabels(site, (site?.trello_labels || []).filter((label) => label.id !== labelId))));
            queryClient.setQueryData(siteKeys.labels(siteId), (response) => response ? ({
                ...response,
                labels: response.labels.map((label) => (label.id === labelId ? { ...label, is_on_card: false } : label)),
                card_labels: response.card_labels.filter((label) => label.id !== labelId),
            }) : response);
            return { previousSite, previousLabels };
        },
        onError: (_error, _labelId, context) => {
            queryClient.setQueryData(siteKeys.detail(siteId), context?.previousSite);
            queryClient.setQueryData(siteKeys.labels(siteId), context?.previousLabels);
        },
        onSuccess: (response) => {
            queryClient.setQueryData(siteKeys.detail(siteId), (site) => replaceSiteLabels(site, response.labels));
            queryClient.setQueryData(siteKeys.labels(siteId), (current) => ({
                labels: (current?.labels || []).map((label) => ({
                    ...label,
                    is_on_card: response.labels.some((cardLabel) => cardLabel.id === label.id),
                })),
                card_labels: response.labels,
                warning: response.warning,
            }));
            // Labels are already written above via setQueryData; only the site detail
            // needs a background refetch. Invalidating siteKeys.all churned every site
            // query for no reason.
            queryClient.invalidateQueries({ queryKey: siteKeys.detail(siteId) });
        },
    });
}
export function useSiteNotesHistory(siteId) {
    return useQuery({
        queryKey: siteKeys.notesHistory(siteId),
        queryFn: () => sites.getNotesHistory(siteId),
        enabled: !!siteId,
    });
}
export function useSiteAttachments(siteId, options = {}) {
    const limit = options.limit ?? 50;
    const offset = options.offset ?? 0;
    return useQuery({
        queryKey: [...siteKeys.attachments(siteId), { limit, offset }],
        queryFn: () => sites.getAttachments(siteId, { limit, offset }),
        enabled: !!siteId,
    });
}
export function useUploadSiteAttachment(siteId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (formData) => sites.uploadAttachment(siteId, formData),
        onSuccess: (newAttachment) => {
            queryClient.setQueriesData({ queryKey: siteKeys.attachments(siteId) }, (old) => {
                if (!old)
                    return old;
                return {
                    ...old,
                    attachments: [newAttachment, ...old.attachments],
                    total: old.total + 1,
                };
            });
            queryClient.invalidateQueries({ queryKey: siteKeys.attachments(siteId) });
            queryClient.invalidateQueries({ queryKey: siteKeys.detail(siteId) });
        },
    });
}
export function useDeleteSiteAttachment(siteId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (attachmentId) => sites.deleteAttachment(attachmentId),
        onSuccess: (_data, attachmentId) => {
            queryClient.setQueriesData({ queryKey: siteKeys.attachments(siteId) }, (old) => {
                if (!old)
                    return old;
                const remaining = old.attachments.filter((a) => a.id !== attachmentId);
                return {
                    ...old,
                    attachments: remaining,
                    total: Math.max(0, old.total - (old.attachments.length - remaining.length)),
                };
            });
            queryClient.invalidateQueries({ queryKey: siteKeys.attachments(siteId) });
            queryClient.invalidateQueries({ queryKey: siteKeys.detail(siteId) });
        },
    });
}
export function useUpdateSiteAttachment(siteId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ attachmentId, attachmentType, }) => sites.updateAttachment(attachmentId, { attachment_type: attachmentType }),
        onMutate: async ({ attachmentId, attachmentType }) => {
            await queryClient.cancelQueries({ queryKey: siteKeys.attachments(siteId) });
            const previous = queryClient.getQueriesData({
                queryKey: siteKeys.attachments(siteId),
            });
            queryClient.setQueriesData({ queryKey: siteKeys.attachments(siteId) }, (old) => {
                if (!old)
                    return old;
                return {
                    ...old,
                    attachments: old.attachments.map((attachment) => attachment.id === attachmentId
                        ? { ...attachment, attachment_type: attachmentType }
                        : attachment),
                };
            });
            return { previous };
        },
        onError: (_err, _variables, context) => {
            context?.previous?.forEach(([key, data]) => {
                queryClient.setQueryData(key, data);
            });
        },
        onSuccess: (updatedAttachment) => {
            queryClient.setQueriesData({ queryKey: siteKeys.attachments(siteId) }, (old) => {
                if (!old)
                    return old;
                return {
                    ...old,
                    attachments: old.attachments.map((attachment) => attachment.id === updatedAttachment.id ? updatedAttachment : attachment),
                };
            });
            queryClient.invalidateQueries({ queryKey: siteKeys.detail(siteId) });
        },
    });
}
//# sourceMappingURL=useSites.js.map